import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { LayoutContext } from "contexts/LayoutProvider"
import Image from "next/image"
import Link from "next/link"
import { useContext } from "react"
import "swiper/css"
import { IconFile, NewText, VideoIcon } from "ui"
import { getUmbracoMediaUrl } from "utils/utils"

const QuarterStyled = styled.div`
  border-left: 1px solid #bad2d4;
  padding: 28px 16px 28px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const Category = styled.ul`
  list-style-type: none;

  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const LinkContainer = styled.li`
  display: flex;
  gap: 4px;
  align-items: center;
`

const StyledLink = styled(Link)`
  text-decoration: none;
  color: #0050c9;
`
const StyledTitle = styled.h3`
  margin: 0;
  display: flex;
  flex-direction: column;
`

const StyledSubTitle = styled.li`
  display: flex;
  gap: 8px;
  align-items: center;
  transform: translateX(-32px);
`

const Line = styled.div`
  width: 28px;
  height: 2px;
  background-color: black;
`
const StyledIcon = styled.div`
  width: 20px;
  height: 20px;
`
const StyledVideoContainer = styled.li`
  position: relative;
  width: 158px;
  height: 92px;
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 120px;
    height: 70px;
  }
  ${(props) => props.theme.breakpoints.up("lg")} {
    width: 180px;
    height: 106px;
  }
`
const PlayButton = styled(Button)`
  margin: 0;
  padding: 0;
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 0;
  width: 51px;
  height: 51px;
  transform: translate(-50%, -50%);
`

function Video({ item, label }: { item: any; label: string }) {
  const { GRAPHQL_URL } = useContext(LayoutContext)
  return (
    <StyledVideoContainer>
      <Image alt="" fill src={"/images/videoimg.png"} />
      <Link
        aria-label={label}
        href={getUmbracoMediaUrl(item.url, GRAPHQL_URL)}
        target="_blank"
      >
        <PlayButton aria-label={`נגן ${label}`}>
          <VideoIcon />
        </PlayButton>
      </Link>
    </StyledVideoContainer>
  )
}

export default function QuarterlyReports({
  el,
  index,
  year,
  locale,
}: {
  el: any
  index: number
  year: number
  locale: string
}) {
  const { GRAPHQL_URL } = useContext(LayoutContext)
  const en = locale === "en"
  const indexString = (indexS: number) => {
    if (!en) {
      if (indexS === 3) {
        return "דו״ח"
      } else {
        return "רבעון"
      }
    } else {
      if (indexS === 3) {
        return "Annual"
      } else {
        return "QTR"
      }
    }
  }
  const yearString = (indexY: number) => {
    if (indexY !== 3) {
      return `0${indexY + 1}`
    } else {
      if (en) {
        return `${String(year).slice(-2)}`
      } else {
        return "שנתי"
      }
    }
  }
  return (
    <QuarterStyled>
      <StyledTitle>
        <NewText
          component={"span"}
          desktopFontSize={14}
          desktopFontWeight={500}
          largeDesktopFontSize={20}
          mobileFontSize={16}
          desktopLineHeight={"19.6px"}
          largeDesktopLineHeight="28px"
          mobileLineHeight={"24px"}
        >
          {indexString(index)}
        </NewText>
        <NewText
          component={"span"}
          desktopFontSize={66}
          desktopFontWeight={500}
          largeDesktopFontSize={100}
          mobileFontSize={64}
          desktopLineHeight={"79.2px"}
          largeDesktopLineHeight="120px"
          mobileLineHeight={"76.8px"}
        >
          {yearString(index)}
        </NewText>
      </StyledTitle>
      {el.financialReports && el.financialReports.length > 0 ? (
        <Category>
          <StyledSubTitle>
            <Line />
            <NewText
              component={"p"}
              desktopFontSize={10}
              desktopFontWeight={500}
              largeDesktopFontSize={14}
              mobileFontSize={14}
              desktopLineHeight={"13.33px"}
              largeDesktopLineHeight="20px"
              mobileLineHeight={"20px"}
            >
              {en ? "Financial reports" : "דוחות כספיים"}
            </NewText>
          </StyledSubTitle>
          {el.financialReports.map((item: any, ind: number) => (
            <LinkContainer key={ind}>
              <StyledIcon>
                <IconFile />
              </StyledIcon>
              <StyledLink
                href={getUmbracoMediaUrl(item.url, GRAPHQL_URL)}
                target="_blank"
                aria-label={`${indexString(index)} ${yearString(index)} ${item.name} PDF`}
              >
                {item.name}
              </StyledLink>
            </LinkContainer>
          ))}
        </Category>
      ) : null}
      {el.investorPresentation && el.investorPresentation.length > 0 ? (
        <Category>
          <StyledSubTitle>
            <Line />
            <NewText
              component={"p"}
              desktopFontSize={10}
              desktopFontWeight={500}
              largeDesktopFontSize={14}
              mobileFontSize={14}
              desktopLineHeight={"13.33px"}
              largeDesktopLineHeight="20px"
              mobileLineHeight={"20px"}
            >
              {en ? "Investor presentation" : "מצגת משקיעים"}
            </NewText>
          </StyledSubTitle>
          {el.investorPresentation.map((item: any, ind: number) => (
            <LinkContainer key={ind}>
              <StyledIcon>
                <IconFile />
              </StyledIcon>
              <StyledLink
                href={getUmbracoMediaUrl(item.url, GRAPHQL_URL)}
                target="_blank"
                aria-label={`${indexString(index)} ${yearString(index)} ${item.name} PDF`}
              >
                {item.name}
              </StyledLink>
            </LinkContainer>
          ))}
        </Category>
      ) : null}
      {el.morePresentations && el.morePresentations.length > 0 ? (
        <Category>
          <StyledSubTitle>
            <Line />
            <NewText
              component={"h3"}
              desktopFontSize={10}
              desktopFontWeight={500}
              largeDesktopFontSize={14}
              mobileFontSize={14}
              desktopLineHeight={"13.33px"}
              largeDesktopLineHeight="20px"
              mobileLineHeight={"20px"}
            >
              {en ? "More presentations" : "מצגות נוספות"}
            </NewText>
          </StyledSubTitle>
          {el.morePresentations.map((item: any, ind: number) => (
            <LinkContainer key={ind}>
              <StyledIcon>
                <IconFile />
              </StyledIcon>
              <StyledLink
                href={getUmbracoMediaUrl(item.url, GRAPHQL_URL)}
                target="_blank"
                aria-label={`${indexString(index)} ${yearString(index)} ${item.name} PDF`}
              >
                {item.name}
              </StyledLink>
            </LinkContainer>
          ))}
        </Category>
      ) : null}
      {el.videos && el.videos.length > 0 ? (
        <Category>
          <StyledSubTitle>
            <Line />

            <NewText
              component={"h3"}
              desktopFontSize={10}
              desktopFontWeight={500}
              largeDesktopFontSize={14}
              mobileFontSize={14}
              desktopLineHeight={"13.33px"}
              largeDesktopLineHeight="20px"
              mobileLineHeight={"20px"}
            >
              {en ? "Quarterly conference call" : "שיחת ועידה רבעונית"}
            </NewText>
          </StyledSubTitle>
          {el.videos.map((item: any, ind: number) => {
            return (
              <Video
                key={ind}
                item={item}
                label={`קובץ שמע לשיחה ולרבעון יעד רבעוני ${indexString(index)} ${yearString(index)}`}
              />
            )
          }
          )}
        </Category>
      ) : null}
      {el.moreVideos && el.moreVideos.length > 0 ? (
        <Category>
          <StyledSubTitle>
            <Line />
            <NewText
              component={"h3"}
              desktopFontSize={10}
              desktopFontWeight={500}
              largeDesktopFontSize={14}
              mobileFontSize={14}
              desktopLineHeight={"13.33px"}
              largeDesktopLineHeight="20px"
              mobileLineHeight={"20px"}
            >
              {en ? "Quarterly conference call" : "שיחת ועידה רבעונית"}
            </NewText>
          </StyledSubTitle>
          {el.moreVideos.map((item: any, ind: number) => {
            return (<Video
              key={ind}
              item={item}
              label={`קובץ שמע לשיחת ועידה רבעונית -  ${indexString(index)} ${yearString(index)}`}
            />)
          }
          )}
        </Category>
      ) : null}
    </QuarterStyled>
  )
}
